import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FiPlay, FiDownload, FiCheck, FiRefreshCw, FiAlertCircle } from "react-icons/fi";
import { BASE_URL } from '../constants/config';
import { drawTeams } from '../utilities/leagueDraw';
import '../styles/editModal.css';
import '../styles/leagueManagement.css';

// Кадр видео. Базово 1280x720, но растёт под контент: шире при большом числе
// групп, выше при большом числе команд в группе. На экране масштабируется.
const BASE_W = 1280;
const BASE_H = 720;
const PAD_X = 50;

const HEADER_H = 112;
const COL_W = 280;
const COL_GAP = 16;
const COL_HEADER_H = 46;
const SLOT_H = 44;
const SLOT_GAP = 8;

// Полоса спонсоров внизу кадра
const SPONSOR_H = 96;
const SPONSOR_LOGO_H = 56;
const SPONSOR_GAP = 28;

const CHIP_W = 210;
const CHIP_H = 38;
const CHIP_GAP = 8;
const POOL_LABEL_H = 30;

// Тайминги одного вытягивания
const INTRO_MS = 1700;
const SPIN_MS = 2400;
const HOLD_MS = 700;
const FLY_MS = 950;
const PICK_MS = SPIN_MS + HOLD_MS + FLY_MS;
const FINAL_MS = 3000;

// Сколько раз выделение перепрыгнет за время рулетки
const SPIN_STEPS = 30;

// Наш логотип берём из public/, а не импортом: в этой сборке нет url-loader,
// и любой import картинки роняет компиляцию
const BRAND_LOGO_URL = '/logo.png';

// Золотой — цвет пометки сеяных
const SEED_COLOR = '#ffc94d';

const PALETTE = ['#667eea', '#f5576c', '#38a169', '#dd6b20', '#3182ce', '#805ad5', '#d53f8c', '#2c7a7b'];

const resolveImage = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    return BASE_URL.url + image;
};

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

const roundRect = (ctx, x, y, w, h, r) => {
    const radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + w, y, x + w, y + h, radius);
    ctx.arcTo(x + w, y + h, x, y + h, radius);
    ctx.arcTo(x, y + h, x, y, radius);
    ctx.arcTo(x, y, x + w, y, radius);
    ctx.closePath();
};

// Пятиконечная звезда — пометка сеяной команды в кадре
const drawStar = (ctx, cx, cy, radius, color) => {
    const spikes = 5;
    const inner = radius * 0.45;
    ctx.save();
    ctx.beginPath();
    for (let i = 0; i < spikes * 2; i++) {
        const r = i % 2 === 0 ? radius : inner;
        const angle = (Math.PI / spikes) * i - Math.PI / 2;
        const px = cx + Math.cos(angle) * r;
        const py = cy + Math.sin(angle) * r;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
    ctx.restore();
};

const fitText = (ctx, text, maxWidth) => {
    if (ctx.measureText(text).width <= maxWidth) return text;
    let result = text;
    while (result.length > 1 && ctx.measureText(result + '…').width > maxWidth) {
        result = result.slice(0, -1);
    }
    return result + '…';
};

// Логотипы грузим с crossOrigin, иначе canvas станет tainted и запись видео
// сломается. Если сервер не отдаёт CORS-заголовок, картинка просто не загрузится
// и вместо неё рисуется кружок с первой буквой — видео при этом работает.
const loadImage = (url) => new Promise(resolve => {
    if (!url) { resolve(null); return; }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
});

const preloadLogos = (teams, leagueImage, sponsors) => Promise.all([
    Promise.all(teams.map(team =>
        loadImage(resolveImage(team.image)).then(img => [team.team_id, img]))),
    loadImage(resolveImage(leagueImage)),
    loadImage(BRAND_LOGO_URL),
    Promise.all((sponsors || []).map(sponsor =>
        loadImage(resolveImage(sponsor.image)).then(img => [sponsor.id, img]))),
]).then(([entries, leagueLogo, brandLogo, sponsorEntries]) => ({
    teams: Object.fromEntries(entries),
    league: leagueLogo,
    brand: brandLogo,
    sponsors: Object.fromEntries(sponsorEntries),
}));

const pickMimeType = () => {
    if (typeof MediaRecorder === 'undefined') return null;
    return ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm']
        .find(type => MediaRecorder.isTypeSupported(type)) || null;
};

// Порядок вытягивания дополняем "рулеткой": последовательностью подсвечиваемых
// команд, которая обязательно заканчивается на той, что реально вытянута
const buildTimeline = (order, teams) => {
    const poolIndexByTeam = {};
    teams.forEach((team, index) => { poolIndexByTeam[team.team_id] = index; });

    return order.map((entry, pickIndex) => {
        const target = poolIndexByTeam[entry.team.team_id];
        const remaining = order.slice(pickIndex).map(e => poolIndexByTeam[e.team.team_id]);
        const others = remaining.filter(i => i !== target);
        const spin = [];
        for (let step = 0; step < SPIN_STEPS - 1; step++) {
            // под конец не подсвечиваем цель раньше времени, иначе спойлер
            const pool = (step > SPIN_STEPS - 5 && others.length > 0) ? others : remaining;
            spin.push(pool[Math.floor(Math.random() * pool.length)]);
        }
        spin.push(target);
        return { ...entry, poolIndex: target, spin };
    });
};

export default function DrawCeremonyModal({
    isOpen, onClose, onApply, teams, groups, leagueName, leagueImage, seasonName,
    sponsors, seededIds,
}) {
    const canvasRef = useRef(null);
    const timerRef = useRef(null);
    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const logosRef = useRef({ teams: {}, league: null, brand: null, sponsors: {} });
    const resultRef = useRef(null);

    const [phase, setPhase] = useState('idle'); // idle | loading | running | done
    const [videoUrl, setVideoUrl] = useState(null);
    const [recordingError, setRecordingError] = useState(null);

    const canRecord = typeof MediaRecorder !== 'undefined'
        && typeof HTMLCanvasElement !== 'undefined'
        && !!HTMLCanvasElement.prototype.captureStream;

    // Размер кадра считаем заранее: и число колонок, и размер самой большой
    // группы, и высота пула известны ещё до жеребьёвки
    const dims = useMemo(() => {
        const groupCount = Math.max(1, groups.length);
        const maxSlots = Math.max(1, Math.ceil(teams.length / groupCount));
        const W = Math.max(BASE_W, PAD_X * 2 + groupCount * COL_W + (groupCount - 1) * COL_GAP);

        const perRow = Math.max(1, Math.floor((W - PAD_X * 2 + CHIP_GAP) / (CHIP_W + CHIP_GAP)));
        const poolRows = Math.max(1, Math.ceil(teams.length / perRow));
        const poolH = POOL_LABEL_H + poolRows * CHIP_H + (poolRows - 1) * CHIP_GAP;

        // Полоса спонсоров занимает место, только если спонсоры есть
        const sponsorH = (sponsors && sponsors.length > 0) ? SPONSOR_H : 0;

        const groupsH = COL_HEADER_H + 14 + maxSlots * SLOT_H + (maxSlots - 1) * SLOT_GAP + 14;
        const H = Math.max(BASE_H, HEADER_H + groupsH + 24 + poolH + 24 + sponsorH);

        return { W, H, perRow, poolH, sponsorH, groupCount };
    }, [groups.length, teams.length, sponsors]);

    const layout = useCallback(() => {
        const colCount = Math.max(1, groups.length);
        const sponsorTop = dims.H - dims.sponsorH;
        const poolTop = sponsorTop - dims.poolH - 24;
        return {
            colW: (dims.W - PAD_X * 2 - COL_GAP * (colCount - 1)) / colCount,
            colTop: HEADER_H,
            colBottom: poolTop - 24,
            poolTop,
            sponsorTop,
        };
    }, [groups.length, dims]);

    const slotPos = (lay, groupIndex, slotIndex) => ({
        x: PAD_X + groupIndex * (lay.colW + COL_GAP),
        y: lay.colTop + COL_HEADER_H + 14 + slotIndex * (SLOT_H + SLOT_GAP),
        w: lay.colW,
        h: SLOT_H,
    });

    const poolPos = (lay, poolIndex) => ({
        x: PAD_X + (poolIndex % dims.perRow) * (CHIP_W + CHIP_GAP),
        y: lay.poolTop + POOL_LABEL_H + Math.floor(poolIndex / dims.perRow) * (CHIP_H + CHIP_GAP),
        w: CHIP_W,
        h: CHIP_H,
    });

    const seededSet = useMemo(
        () => new Set((seededIds || []).map(Number)),
        [seededIds]
    );
    const isSeeded = (teamId) => seededSet.has(Number(teamId));

    // Логотип вписывается в квадрат целиком, без обрезки и без круглой маски:
    // у клубных эмблем часто нестандартные пропорции, круг их резал бы
    const drawLogo = (ctx, img, name, x, y, size, accent) => {
        if (img && img.width > 0 && img.height > 0) {
            const scale = Math.min(size / img.width, size / img.height);
            const w = img.width * scale;
            const h = img.height * scale;
            ctx.drawImage(img, x + (size - w) / 2, y + (size - h) / 2, w, h);
            return;
        }
        ctx.fillStyle = accent;
        roundRect(ctx, x, y, size, size, size * 0.18);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.font = `700 ${Math.round(size * 0.5)}px Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText((name || '?').charAt(0).toUpperCase(), x + size / 2, y + size / 2);
    };

    const drawTeamCard = (ctx, team, x, y, w, h, accent, scale) => {
        const big = scale === 'big';
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.35)';
        ctx.shadowBlur = big ? 28 : 10;
        ctx.shadowOffsetY = big ? 10 : 4;
        ctx.fillStyle = big ? '#ffffff' : 'rgba(255,255,255,0.94)';
        roundRect(ctx, x, y, w, h, big ? 18 : 10);
        ctx.fill();
        ctx.restore();

        ctx.save();
        roundRect(ctx, x, y, w, h, big ? 18 : 10);
        ctx.clip();
        ctx.fillStyle = accent;
        ctx.fillRect(x, y, big ? 8 : 5, h);
        ctx.restore();

        const logoSize = h * 0.66;
        const logoX = x + (big ? 24 : 14);
        const logoY = y + (h - logoSize) / 2;
        drawLogo(ctx, logosRef.current.teams[team.team_id], team.name, logoX, logoY, logoSize, accent);

        ctx.fillStyle = '#1a202c';
        ctx.font = `${big ? 700 : 600} ${Math.round(big ? 34 : Math.min(20, h * 0.42))}px Arial, sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        let textX = logoX + logoSize + (big ? 20 : 12);
        if (isSeeded(team.team_id)) {
            const starR = big ? 13 : 7;
            drawStar(ctx, textX + starR, y + h / 2, starR, SEED_COLOR);
            textX += starR * 2 + (big ? 12 : 7);
        }
        ctx.fillText(
            fitText(ctx, team.name, x + w - textX - (big ? 24 : 12)),
            textX,
            y + h / 2
        );
    };

    // Команда в нижнем пуле. highlight: 0 - обычная, 1 - под выделением
    const drawPoolChip = (ctx, team, x, y, w, h, highlight) => {
        const grow = highlight * 4;
        const cx = x - grow;
        const cy = y - grow;
        const cw = w + grow * 2;
        const ch = h + grow * 2;

        if (highlight > 0) {
            ctx.save();
            ctx.shadowColor = `rgba(246, 173, 85, ${0.55 * highlight})`;
            ctx.shadowBlur = 22 * highlight;
            ctx.fillStyle = '#ffffff';
            roundRect(ctx, cx, cy, cw, ch, 9);
            ctx.fill();
            ctx.restore();
            ctx.strokeStyle = '#f6ad55';
            ctx.lineWidth = 2.5;
            roundRect(ctx, cx, cy, cw, ch, 9);
            ctx.stroke();
        } else if (isSeeded(team.team_id)) {
            ctx.fillStyle = 'rgba(255, 201, 77, 0.14)';
            roundRect(ctx, cx, cy, cw, ch, 9);
            ctx.fill();
            ctx.strokeStyle = 'rgba(255, 201, 77, 0.5)';
            ctx.lineWidth = 1.5;
            roundRect(ctx, cx, cy, cw, ch, 9);
            ctx.stroke();
        } else {
            ctx.fillStyle = 'rgba(255,255,255,0.12)';
            roundRect(ctx, cx, cy, cw, ch, 9);
            ctx.fill();
        }

        const logoSize = ch * 0.66;
        const logoX = cx + 9;
        const logoY = cy + (ch - logoSize) / 2;
        drawLogo(ctx, logosRef.current.teams[team.team_id], team.name, logoX, logoY, logoSize, '#4a5568');

        ctx.fillStyle = highlight > 0.5 ? '#1a202c' : '#e2e8f0';
        ctx.font = `600 15px Arial, sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        let textX = logoX + logoSize + 9;
        if (isSeeded(team.team_id)) {
            drawStar(ctx, textX + 6, cy + ch / 2, 6, SEED_COLOR);
            textX += 17;
        }
        ctx.fillText(fitText(ctx, team.name, cx + cw - textX - 10), textX, cy + ch / 2);
    };

    const renderFrame = useCallback((elapsed) => {
        const canvas = canvasRef.current;
        const result = resultRef.current;
        if (!canvas || !result) return false;
        const ctx = canvas.getContext('2d');
        const { timeline } = result;
        const lay = layout();
        const { W, H } = dims;

        const bg = ctx.createLinearGradient(0, 0, W, H);
        bg.addColorStop(0, '#1a202c');
        bg.addColorStop(1, '#2d3748');
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, W, H);

        // Шапка: логотип лиги слева от названия
        const logoSize = 62;
        ctx.font = '700 40px Arial, sans-serif';
        const titleText = fitText(ctx, leagueName, W - 300);
        const titleW = ctx.measureText(titleText).width;
        const blockW = logoSize + 20 + titleW;
        const blockX = (W - blockW) / 2;
        drawLogo(ctx, logosRef.current.league, leagueName, blockX, 20, logoSize, '#667eea');

        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(titleText, blockX + logoSize + 20, 44);
        ctx.fillStyle = '#a0aec0';
        ctx.font = '600 21px Arial, sans-serif';
        ctx.fillText(`Жеребьёвка · сезон ${seasonName}`, blockX + logoSize + 20, 76);

        // Легенда — иначе непонятно, что означает звёздочка
        if (seededSet.size > 0) {
            const legendY = 76;
            const legendText = '— сеяные';
            ctx.font = '600 15px Arial, sans-serif';
            const legendW = ctx.measureText(legendText).width;
            const legendX = W - PAD_X - legendW;
            drawStar(ctx, legendX - 14, legendY, 7, SEED_COLOR);
            ctx.fillStyle = '#a0aec0';
            ctx.textAlign = 'left';
            ctx.fillText(legendText, legendX, legendY);
        }

        // Наш логотип в правом верхнем углу
        const brand = logosRef.current.brand;
        if (brand && brand.width > 0) {
            const brandH = 54;
            const brandW = brand.width * (brandH / brand.height);
            ctx.drawImage(brand, W - PAD_X - brandW, 26, brandW, brandH);
        }

        // Колонки групп
        groups.forEach((group, gi) => {
            const accent = PALETTE[gi % PALETTE.length];
            const x = PAD_X + gi * (lay.colW + COL_GAP);
            ctx.fillStyle = 'rgba(255,255,255,0.07)';
            roundRect(ctx, x, lay.colTop, lay.colW, lay.colBottom - lay.colTop, 14);
            ctx.fill();

            ctx.fillStyle = accent;
            roundRect(ctx, x, lay.colTop, lay.colW, COL_HEADER_H, 14);
            ctx.fill();
            ctx.fillStyle = '#ffffff';
            ctx.font = '700 21px Arial, sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(
                fitText(ctx, group.name, lay.colW - 20),
                x + lay.colW / 2,
                lay.colTop + COL_HEADER_H / 2
            );
        });

        // Определяем текущий шаг
        let placedCount;
        let current = null;
        if (elapsed < INTRO_MS) {
            placedCount = 0;
        } else {
            const pickIndex = Math.floor((elapsed - INTRO_MS) / PICK_MS);
            if (pickIndex >= timeline.length) {
                placedCount = timeline.length;
            } else {
                placedCount = pickIndex;
                current = {
                    entry: timeline[pickIndex],
                    local: (elapsed - INTRO_MS) % PICK_MS,
                };
            }
        }

        // Уже разложенные команды
        for (let i = 0; i < placedCount; i++) {
            const entry = timeline[i];
            const pos = slotPos(lay, entry.groupIndex, entry.slotIndex);
            drawTeamCard(ctx, entry.team, pos.x, pos.y, pos.w, pos.h,
                PALETTE[entry.groupIndex % PALETTE.length], 'small');
        }

        // Нижний пул: команды, которые ещё не вытянули
        const flying = current && current.local >= SPIN_MS + HOLD_MS;
        const poolFrom = flying ? placedCount + 1 : placedCount;
        const inPool = timeline.slice(poolFrom);

        ctx.fillStyle = '#a0aec0';
        ctx.font = '600 17px Arial, sans-serif';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        // Пока тянем сеяных, подписываем корзину — так понятнее, что происходит
        const potLabel = current && current.entry.pot === 'seeded'
            ? 'Корзина сеяных'
            : (seededIds && seededIds.length > 0 ? 'Корзина несеяных' : 'Команды');
        ctx.fillText(
            inPool.length > 0
                ? `${potLabel} · осталось ${inPool.length}`
                : 'Все команды разобраны',
            PAD_X,
            lay.poolTop + POOL_LABEL_H / 2
        );

        let highlightPoolIndex = null;
        let highlightStrength = 0;
        if (current && current.local < SPIN_MS) {
            const p = current.local / SPIN_MS;
            const step = Math.min(SPIN_STEPS - 1, Math.floor(SPIN_STEPS * easeOutCubic(p)));
            highlightPoolIndex = current.entry.spin[step];
            highlightStrength = 1;
        } else if (current && current.local < SPIN_MS + HOLD_MS) {
            highlightPoolIndex = current.entry.poolIndex;
            // мягкая пульсация на удержании
            const p = (current.local - SPIN_MS) / HOLD_MS;
            highlightStrength = 0.75 + 0.25 * Math.sin(p * Math.PI * 3);
        }

        inPool.forEach(entry => {
            const pos = poolPos(lay, entry.poolIndex);
            const isHighlighted = entry.poolIndex === highlightPoolIndex;
            drawPoolChip(ctx, entry.team, pos.x, pos.y, pos.w, pos.h,
                isHighlighted ? highlightStrength : 0);
        });

        // Полёт вытянутой команды из пула в группу
        if (flying) {
            const { entry, local } = current;
            const accent = PALETTE[entry.groupIndex % PALETTE.length];
            const from = poolPos(lay, entry.poolIndex);
            const target = slotPos(lay, entry.groupIndex, entry.slotIndex);
            const p = easeInOutCubic((local - SPIN_MS - HOLD_MS) / FLY_MS);

            // на середине пути карточка крупнее — читается название
            const lift = Math.sin(p * Math.PI);
            const boost = 1 + lift * 0.85;
            const w = (from.w + (target.w - from.w) * p) * boost;
            const h = (from.h + (target.h - from.h) * p) * boost;
            const cx = from.x + from.w / 2 + (target.x + target.w / 2 - from.x - from.w / 2) * p;
            const cy = from.y + from.h / 2 + (target.y + target.h / 2 - from.y - from.h / 2) * p
                - lift * 40;

            drawTeamCard(ctx, entry.team, cx - w / 2, cy - h / 2, w, h, accent,
                lift > 0.45 ? 'big' : 'small');
        }

        return elapsed < INTRO_MS + timeline.length * PICK_MS + FINAL_MS;
    }, [groups, layout, dims, leagueName, seasonName, sponsors, seededIds, seededSet]);

    // Дорожку captureStream надо гасить руками: после остановки записи браузер
    // продолжает тянуть с холста кадры и жечь процессор
    const releaseStream = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
    }, []);

    const stopEverything = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (recorderRef.current && recorderRef.current.state === 'recording') {
            recorderRef.current.stop();
        }
        releaseStream();
    }, [releaseStream]);

    const start = async () => {
        setPhase('loading');
        setRecordingError(null);
        if (videoUrl) {
            URL.revokeObjectURL(videoUrl);
            setVideoUrl(null);
        }

        logosRef.current = await preloadLogos(teams, leagueImage, sponsors);
        const { order, assignment } = drawTeams(teams, groups, seededIds);
        resultRef.current = { assignment, timeline: buildTimeline(order, teams) };

        const canvas = canvasRef.current;
        if (!canvas) return;
        renderFrame(0);

        // Запись стартуем до анимации, чтобы попал первый кадр
        chunksRef.current = [];
        const mimeType = pickMimeType();
        if (canRecord && mimeType) {
            try {
                const stream = canvas.captureStream(30);
                streamRef.current = stream;
                // Битрейт ограничиваем, иначе длинный ролик в большом разрешении
                // раздувает блоб в памяти на сотни мегабайт
                const recorder = new MediaRecorder(stream, {
                    mimeType,
                    videoBitsPerSecond: 2500000,
                });
                recorder.ondataavailable = (e) => {
                    if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
                };
                recorder.onstop = () => {
                    const blob = new Blob(chunksRef.current, { type: 'video/webm' });
                    // Куски больше не нужны — Blob держит данные сам
                    chunksRef.current = [];
                    releaseStream();
                    setVideoUrl(URL.createObjectURL(blob));
                };
                // Пишем кусками, чтобы память набиралась постепенно,
                // а не одним большим куском в конце
                recorder.start(2000);
                recorderRef.current = recorder;
            } catch (e) {
                console.error('Не удалось начать запись жеребьёвки:', e);
                setRecordingError('Записать видео не получилось, но жеребьёвка пройдёт как обычно.');
                recorderRef.current = null;
            }
        } else {
            recorderRef.current = null;
            setRecordingError('Браузер не умеет записывать видео с canvas — жеребьёвка пройдёт без записи.');
        }

        setPhase('running');
        const startedAt = Date.now();
        timerRef.current = setInterval(() => {
            if (renderFrame(Date.now() - startedAt)) return;
            clearInterval(timerRef.current);
            timerRef.current = null;
            if (recorderRef.current && recorderRef.current.state === 'recording') {
                recorderRef.current.stop();
            }
            setPhase('done');
        }, 1000 / 30);
    };

    const handleApply = () => {
        if (!resultRef.current) return;
        onApply(resultRef.current.assignment);
        onClose();
    };

    const fileName = `zherebyovka-${leagueName}-${seasonName}`
        .replace(/[^\wа-яА-ЯёЁ-]+/g, '-')
        .replace(/-+/g, '-')
        .toLowerCase();

    const durationSec = Math.round((INTRO_MS + teams.length * PICK_MS + FINAL_MS) / 1000);

    // Компонент остаётся в дереве и после закрытия модалки, поэтому при закрытии
    // руками отпускаем всё тяжёлое: видео, загруженные логотипы, раскладку
    useEffect(() => {
        if (isOpen) {
            setPhase('idle');
            setRecordingError(null);
            resultRef.current = null;
            return undefined;
        }
        stopEverything();
        chunksRef.current = [];
        logosRef.current = { teams: {}, league: null, brand: null, sponsors: {} };
        resultRef.current = null;
        setVideoUrl(prev => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });
        return undefined;
    }, [isOpen, stopEverything]);

    // Перезагрузка посреди записи теряет дубль — предупреждаем
    useEffect(() => {
        if (phase !== 'running') return undefined;
        const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [phase]);

    useEffect(() => () => {
        stopEverything();
        chunksRef.current = [];
        logosRef.current = { teams: {}, league: null, brand: null, sponsors: {} };
    }, [stopEverything]);

    useEffect(() => () => {
        if (videoUrl) URL.revokeObjectURL(videoUrl);
    }, [videoUrl]);

    if (!isOpen) return null;

    const busy = phase === 'loading' || phase === 'running';

    return (
        <div className="modalOverlay" onClick={busy ? undefined : onClose}>
            <div
                className="modalContent modalContentCeremony"
                style={{ maxWidth: groups.length > 4 ? 1400 : 1040 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modalHeader">
                    <h2>Жеребьёвка</h2>
                    <button className="modalClose" onClick={onClose} disabled={busy}>×</button>
                </div>

                <div className="ceremonyBody">
                    <canvas
                        ref={canvasRef}
                        width={dims.W}
                        height={dims.H}
                        className="ceremonyCanvas"
                    />

                    {phase === 'idle' && (
                        <div className="ceremonyOverlay">
                            <p className="ceremonyLead">
                                {teams.length} команд разойдутся по {groups.length}{' '}
                                {groups.length === 1 ? 'группе' : 'группам'} поровну.
                            </p>
                            <p className="ceremonyHint">
                                Команды вытягиваются по одной из нижнего списка.
                                Идёт запись — по окончании видео можно скачать.
                                Длительность около {durationSec} сек.
                            </p>
                        </div>
                    )}

                    {phase === 'loading' && (
                        <div className="ceremonyOverlay">
                            <p className="ceremonyLead">Готовим жеребьёвку…</p>
                        </div>
                    )}
                </div>

                {recordingError && (
                    <div className="ceremonyNotice">
                        <FiAlertCircle style={{ marginRight: '8px', flexShrink: 0 }} />
                        {recordingError}
                    </div>
                )}

                <div className="modalFooter">
                    {phase === 'done' && videoUrl && (
                        <a
                            className="modalButton modalButtonGhost"
                            href={videoUrl}
                            download={`${fileName}.webm`}
                        >
                            <FiDownload style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                            Скачать видео
                        </a>
                    )}
                    <button className="modalButton modalButtonCancel" onClick={onClose} disabled={busy}>
                        Закрыть
                    </button>
                    {phase === 'done' ? (
                        <>
                            <button className="modalButton modalButtonCancel" onClick={start}>
                                <FiRefreshCw style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                Пережеребить
                            </button>
                            <button className="modalButton modalButtonSave" onClick={handleApply}>
                                <FiCheck style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                Применить результат
                            </button>
                        </>
                    ) : (
                        <button
                            className="modalButton modalButtonSave"
                            onClick={start}
                            disabled={busy}
                        >
                            <FiPlay style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                            {phase === 'running' ? 'Идёт жеребьёвка…' : 'Начать жеребьёвку'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
